<template>
    <data-table
        :additional-columns="additionalColumns"
        :auth="auth"
        :collection="collection"
        :delete-modal-title="$t('budget.delete_confirm')"
        :is-loading="isLoading"
        has-comparison-columns
    >
        <template v-slot:column-header="slot">
            {{ $moment(slot.column.entity.date).format('MMMM') }}
        </template>
    </data-table>
</template>

<script>
import DataTable from '@/Components/Storemanager/DataTable';
import DataColumn from '@/Components/Storemanager/Entities/DataColumn';
import DataColumnCollection from '@/Components/Storemanager/Entities/DataColumnCollection';
import Store from '@/Models/Store';
import Review from '@/Models/Review';
import moment from 'moment';
import BudgetProjection from '@/Models/BudgetProjection';
import Actual from '@/Models/Actual';
import FieldsModel from '@/Models/Contracts/FieldsModel';
import BudgetProjectionEntry from '@/Models/BudgetProjectionEntry';

export default {
    components: {DataTable},

    props: {
        review: Review,
        store: Store,
        reviewable: Object,
    },

    metaInfo() {
        return {
            title: this.$t('general.review') +
                (!!this.budget
                    ? (this.$t('general.budget-projection') + this.budget.name + ' ' + this.budget.year)
                    : ''),
        };
    },

    data: function() {
        return {
            collection: null,
            budgetProjection: null,
            actuals: null,
            isLoading: true,
        };
    },

    created() {
        let budgetProjectionRequest = BudgetProjection.with(['entries', 'reviews']).$find(this.reviewable.id);
        let allMonths = _.range(0, 12).map(num => moment.utc().month(num).startOf('month').format('YYYY-MM-DD'));
        let actualsRequest = Actual.where('store_id', this.store.id).whereIn('date', allMonths).$get();

        Promise.all([budgetProjectionRequest, actualsRequest]).then(responses => {
            this.budgetProjection = responses[0];
            this.actuals = responses[1];
            this.isLoading = false;
            this.$emit('update:locked', this.budgetProjection.is_locked);
            this.createCollection();
        });
    },

    computed: {
        additionalColumns() {
            if (!this.accumulatedColumn) {
                return null;
            }

            return new DataColumnCollection([this.accumulatedColumn], true);
        },

        accumulatedColumn() {
            if (!this.collection || this.collection.columns.length === 0) {
                return null;
            }

            let entity = new FieldsModel({store: this.store}, false);

            entity.add(this.collection.columns.map(column => column.entity), false);

            const column = new DataColumn(
                entity,
                true,
            );

            column.header = this.$t('general.overall').toString();

            return column;
        },
    },

    methods: {
        createCollection() {
            let collection = [];
            let monthRange = _.range(1, 13);

            for (const monthIndex of monthRange) {
                let column = new DataColumn();

                if (this.actuals) {
                    column.entity = this.actuals.find(
                        actual => moment.utc(actual.date)
                            .isSame(moment.utc(monthIndex + ' ' + this.budgetProjection.year, 'M YYYY'), 'month'),
                    );

                    if (column.entity) {
                        column.header = `
                        <span>${moment(column.entity.date).format('MMMM')} (${this.$t('general.actual')})</span>
                        `;
                    }
                }

                if (!column.entity && this.budgetProjection.entries && this.budgetProjection.entries.length > 0) {
                    column.entity = this.budgetProjection.entries.find(
                        entry => moment.utc(entry.date)
                            .isSame(moment.utc(monthIndex + ' ' + this.budgetProjection.year, 'M YYYY'), 'month'),
                    );
                }

                if (!column.entity) {
                    column.entity = new BudgetProjectionEntry({
                        store: this.store,
                        date: moment.utc(monthIndex + '-' + this.budgetProjection.year, 'M-Y')
                            .startOf('month')
                            .format('YYYY-MM-DD'),
                    }).for(this.budgetProjection);
                }

                collection.push(column);
            }

            this.collection = new DataColumnCollection(
                collection,
                (this.budgetProjection?.is_locked
                    ? this.budgetProjection?.is_locked
                    : this.budgetProjection?.is_in_review)
                ?? false,
            );
        },
    },
};
</script>
